import {BaseBadge, BaseButton, BaseCard, BasePagination, BaseTable} from "@/components";
import type {Column} from "@/components/BaseTable/BaseTable";
import {useUsers, useUsersStats} from "@/hooks/useUsers";
import {StatsCard} from "@/pages/Dashboard/Home/components/StatsCard";
import type {AccountStatus, KycStatus, User} from "@/types/user.types";
import {CheckCircle, Clock, Eye, Users as UsersIcon} from "lucide-react";
import {useState} from "react";
import {CreateUserModal} from "./components/CreateUserModal";
import {UserDetailModal} from "./components/UserDetailModal";
import {UserFilters} from "./components/UserFilters";

function kycBadgeVariant(status: KycStatus): "success" | "danger" | "warning" | "info" | "neutral" {
  const map: Record<KycStatus, "success" | "danger" | "warning" | "info" | "neutral"> = {
    approved: "success",
    pending: "warning",
    in_progress: "info",
    failed: "danger",
    rejected: "danger",
    expired: "neutral",
  };
  return map[status];
}

function kycLabel(status: KycStatus): string {
  const map: Record<KycStatus, string> = {
    approved: "Aprobado",
    pending: "Pendiente",
    in_progress: "En proceso",
    failed: "Fallido",
    rejected: "Rechazado",
    expired: "Expirado",
  };
  return map[status];
}

function accountStatusBadge(status: AccountStatus): "success" | "danger" | "warning" {
  const map: Record<AccountStatus, "success" | "danger" | "warning"> = {
    active: "success",
    suspended: "warning",
    blocked: "danger",
  };
  return map[status];
}

function accountStatusLabel(status: AccountStatus): string {
  const map: Record<AccountStatus, string> = {
    active: "Activo",
    suspended: "Suspendido",
    blocked: "Bloqueado",
  };
  return map[status];
}

function formatDate(iso: string): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("es-CL", {dateStyle: "short"}).format(new Date(iso));
}

export function Users() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [kycFilter, setKycFilter] = useState<string>("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const filters = {
    page,
    limit,
    ...(search ? {search} : {}),
    ...(statusFilter !== "all" ? {status: statusFilter} : {}),
    ...(kycFilter !== "all" ? {kycStatus: kycFilter} : {}),
  };

  const {data, isLoading} = useUsers(filters);
  const {data: stats} = useUsersStats();

  const users = data?.users ?? [];
  const pagination = data?.pagination ?? {total: 0, page: 1, limit: 20, totalPages: 1};

  const columns: Column<User>[] = [
    {
      key: "fullName",
      header: "Nombre",
      render: row => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-(--color-primary) flex items-center justify-center text-white text-xs font-bold shrink-0">
            {row.fullName.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-medium text-(--color-text-primary)">{row.fullName}</p>
            <p className="text-xs text-(--color-text-tertiary)">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: "country",
      header: "País",
      render: row => <span className="text-sm text-(--color-text-secondary)">{row.country}</span>,
    },
    {
      key: "kycStatus",
      header: "KYC",
      render: row => (
        <BaseBadge
          variant={kycBadgeVariant(row.kycStatus)}
          label={kycLabel(row.kycStatus)}
          dot
        />
      ),
    },
    {
      key: "accountStatus",
      header: "Estado",
      render: row => (
        <BaseBadge
          variant={accountStatusBadge(row.accountStatus)}
          label={accountStatusLabel(row.accountStatus)}
          dot
        />
      ),
    },
    {
      key: "createdAt",
      header: "Registro",
      render: row => <span className="text-xs text-(--color-text-tertiary)">{formatDate(row.createdAt)}</span>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: row => (
        <BaseButton
          variant="ghost"
          size="sm"
          onClick={e => {
            e.stopPropagation();
            setSelectedUser(row);
          }}
          title="Ver detalle"
        >
          <Eye size={15} />
        </BaseButton>
      ),
    },
  ];

  const handleFilterReset = () => {
    setSearch("");
    setStatusFilter("all");
    setKycFilter("all");
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-(--color-text-primary)">Usuarios</h1>
          <p className="text-sm text-(--color-text-secondary) mt-1">{pagination.total} usuarios registrados</p>
        </div>
        <BaseButton
          variant="primary"
          size="md"
          onClick={() => setShowCreateModal(true)}
        >
          + Nuevo usuario
        </BaseButton>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatsCard
            label="Total Usuarios"
            value={stats.totalUsers.toLocaleString()}
            icon={<UsersIcon size={20} />}
            iconColor="var(--color-primary)"
          />
          <StatsCard
            label="Activos"
            value={stats?.activeUsers?.toLocaleString()}
            icon={<CheckCircle size={20} />}
            iconColor="var(--color-success)"
          />
          <StatsCard
            label="KYC Pendiente"
            value={stats?.kycPending?.toLocaleString()}
            icon={<Clock size={20} />}
            iconColor="var(--color-warning)"
          />
          <StatsCard
            label="KYC Aprobado"
            value={stats?.kycApproved?.toLocaleString()}
            icon={<CheckCircle size={20} />}
            iconColor="var(--color-accent)"
          />
        </div>
      )}

      {/* Filters */}
      <BaseCard padding="md">
        <UserFilters
          search={search}
          statusFilter={statusFilter}
          kycFilter={kycFilter}
          onSearchChange={v => {
            setSearch(v);
            setPage(1);
          }}
          onStatusChange={v => {
            setStatusFilter(v);
            setPage(1);
          }}
          onKycChange={v => {
            setKycFilter(v);
            setPage(1);
          }}
          onReset={handleFilterReset}
        />
      </BaseCard>

      {/* Table */}
      <BaseCard padding="none">
        <BaseTable<User>
          data={users}
          columns={columns}
          loading={isLoading}
          emptyMessage="No se encontraron usuarios con los filtros seleccionados."
          onRowClick={row => setSelectedUser(row)}
        />
        {pagination.totalPages > 1 && (
          <BasePagination
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={limit}
            onPageChange={setPage}
          />
        )}
      </BaseCard>

      {/* Create Modal */}
      {showCreateModal && <CreateUserModal onClose={() => setShowCreateModal(false)} />}

      {/* User Detail Modal */}
      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          open={!!selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
}
